import React, { useState, useCallback } from 'react';
import ReportModal from './ReportModal';
import './MessageActions.css';

// Per-message action row — mirrors what users get on Gemini / ChatGPT:
//   Assistant turns: 👍  👎  🔁  📋   (regenerate + copy + reactions)
//   User turns:     ✏️  📋             (edit + copy)
//
// `text` is the bubble text used for copy.
// `onLike` / `onDislike` flip a local visual state and (optionally) phone
// home if the parent wants to log feedback.
// `onRegenerate` rebuilds the assistant turn from the last user prompt
// (only meaningful on assistant rows; parent decides what that means).
// `onEdit` is called for user rows so the parent can swap the bubble
// for an inline editor.
//
// All buttons are tiny, ghost-styled, and only show on hover/focus so
// they don't add visual noise to a long thread.

const Icon = {
  thumbUp:   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M2 21h4V9H2v12zM23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73V10z"/></svg>,
  thumbDown: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M22 3h-4v12h4V3zM1 14c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.58-6.59c.37-.36.59-.86.59-1.41V5c0-1.1-.9-2-2-2H6c-.83 0-1.54.5-1.84 1.22L1.14 11.27c-.09.23-.14.47-.14.73V14z"/></svg>,
  refresh:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  copy:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  check:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  pencil:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  flag:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
};

function ActionBtn({ children, label, onClick, active = false, disabled = false, tone = 'neutral' }) {
  return (
    <button
      type="button"
      className={`msg-act msg-act-${tone} ${active ? 'is-on' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

export default function MessageActions({
  role,                // 'user' | 'assistant'
  text,                // plain text for copy
  onLike, onDislike,
  onRegenerate,
  onEdit,
  canRegenerate = true,
  reportKind = 'chat',   // 'chat' | 'companion' — surface for content reports
  reportMeta = {},       // extra context (assistant id, persona…)
  className = '',
}) {
  const [vote, setVote] = useState(null);   // 'like' | 'dislike' | null
  const [copied, setCopied] = useState(false);
  const [reporting, setReporting] = useState(false);

  const doCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Fallback: select-and-execCommand path; fail silently otherwise.
    }
  }, [text]);

  const doLike = () => {
    const next = vote === 'like' ? null : 'like';
    setVote(next);
    onLike?.(next);
  };
  const doDislike = () => {
    const next = vote === 'dislike' ? null : 'dislike';
    setVote(next);
    onDislike?.(next);
  };

  if (role === 'user') {
    return (
      <div className={`msg-actions msg-actions-user ${className}`}>
        {onEdit && (
          <ActionBtn label="Edit" onClick={onEdit}>{Icon.pencil}</ActionBtn>
        )}
        <ActionBtn label={copied ? 'Copied' : 'Copy'} onClick={doCopy} active={copied} tone="ok">
          {copied ? Icon.check : Icon.copy}
        </ActionBtn>
      </div>
    );
  }

  return (
    <div className={`msg-actions msg-actions-ai ${className}`}>
      <ActionBtn label="Good response" onClick={doLike} active={vote === 'like'} tone="like">
        {Icon.thumbUp}
      </ActionBtn>
      <ActionBtn label="Bad response" onClick={doDislike} active={vote === 'dislike'} tone="dislike">
        {Icon.thumbDown}
      </ActionBtn>
      {onRegenerate && (
        <ActionBtn label="Regenerate" onClick={onRegenerate} disabled={!canRegenerate}>
          {Icon.refresh}
        </ActionBtn>
      )}
      <ActionBtn label={copied ? 'Copied' : 'Copy'} onClick={doCopy} active={copied} tone="ok">
        {copied ? Icon.check : Icon.copy}
      </ActionBtn>
      {/* Report / flag — required so users can flag offensive
          AI-generated replies (Google Play AI content policy). */}
      <ActionBtn label="Report" onClick={() => setReporting(true)} tone="dislike">
        {Icon.flag}
      </ActionBtn>
      <ReportModal
        open={reporting}
        onClose={() => setReporting(false)}
        kind={reportKind}
        content={text || ''}
        meta={reportMeta}
      />
    </div>
  );
}
